import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import api from '../api';

export default function DataImport() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const text = await file.text();
      const backup = JSON.parse(text);

      if (!backup.version) {
        throw new Error('无效的备份文件格式');
      }

      const response = await api.post('/api/backup/restore', { backup });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '导入失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/backup/json', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('导出失败: ' + (err.message || '未知错误'));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>数据导入/导出</Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>导出数据</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            将所有数据导出为 JSON 文件，可用于备份或迁移到其他设备。
          </Typography>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={loading}
          >
            导出 JSON 备份
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>导入数据</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            从 JSON 备份文件导入数据。注意：导入会覆盖现有数据！
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            警告：导入操作会清空当前所有数据，请确保已备份重要数据！
          </Alert>

          <Button
            variant="contained"
            component="label"
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
            disabled={loading}
          >
            选择备份文件
            <input
              type="file"
              hidden
              accept=".json"
              onChange={handleFileUpload}
            />
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <CheckIcon color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
              导入成功
            </Typography>
            <Typography variant="body2" paragraph>
              {result.message}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>导入统计：</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`项目: ${result.counts?.projects || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`工人: ${result.counts?.workers || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`记工: ${result.counts?.workLogs || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`材料: ${result.counts?.materials || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`杂费: ${result.counts?.expenses || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`收款: ${result.counts?.incomes || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`工人调整: ${result.counts?.adjustments || 0} 条`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary={`报价单: ${result.counts?.quotations || 0} 条`} />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
